import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: February 21, 2026",
      backHome: "Back to Home",
      sections: [
        {
          icon: Shield,
          title: "Introduction",
          content: `Welcome to Canada English Life Academy ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.canada-englishlife.com and use our services.

Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.`
        },
        {
          icon: Database,
          title: "Information We Collect",
          content: `We collect information that you voluntarily provide to us when you:
• Register for an account
• Enroll in courses
• Book assessment sessions
• Subscribe to our newsletter
• Contact us through our website
• Participate in quizzes or assessments

This information may include:
• Name and contact information (email, phone number)
• Payment information (processed securely through third-party providers)
• Educational preferences and goals
• Communication preferences
• Assessment results and course progress`
        },
        {
          icon: Eye,
          title: "How We Use Your Information",
          content: `We use the information we collect to:
• Provide, operate, and maintain our services
• Process transactions and send related information
• Send administrative information, updates, and promotional materials
• Respond to inquiries and offer customer support
• Personalize your learning experience
• Improve our website and services
• Comply with legal obligations
• Communicate with you via WhatsApp or email about your courses and assessments`
        },
        {
          icon: Lock,
          title: "Data Security",
          content: `We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
• Secure SSL encryption for data transmission
• Regular security assessments
• Limited access to personal data by authorized personnel only
• Secure data storage practices

However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`
        },
        {
          icon: Globe,
          title: "Third-Party Services",
          content: `We may share your information with third-party service providers who assist us in operating our website and conducting our business, including:
• Payment processors (for secure payment handling)
• Email service providers (for communication)
• Analytics providers (to improve our services)
• Video conferencing platforms (for online classes)

These third parties are obligated to keep your information confidential and use it only for the purposes for which we disclose it to them.`
        },
        {
          icon: Mail,
          title: "Your Rights & Contact Us",
          content: `You have the right to:
• Access your personal information
• Correct inaccurate data
• Request deletion of your data
• Opt-out of marketing communications
• Withdraw consent at any time

For any questions about this Privacy Policy or to exercise your rights, please contact us:

Email: info@canada-englishlife.com
Phone: +1 647 874 5620
Address: Canada

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.`
        }
      ]
    },
    ar: {
      title: "سياسة الخصوصية",
      lastUpdated: "آخر تحديث: 21 فبراير 2026",
      backHome: "العودة للرئيسية",
      sections: [
        {
          icon: Shield,
          title: "مقدمة",
          content: `مرحباً بكم في أكاديمية Canada English Life ("نحن" أو "لنا"). نحن ملتزمون بحماية معلوماتك الشخصية وحقك في الخصوصية. توضح سياسة الخصوصية هذه كيفية جمع واستخدام والإفصاح عن معلوماتك وحمايتها عند زيارة موقعنا www.canada-englishlife.com واستخدام خدماتنا.

يرجى قراءة سياسة الخصوصية هذه بعناية. إذا كنت لا توافق على شروط سياسة الخصوصية هذه، يرجى عدم الوصول إلى الموقع.`
        },
        {
          icon: Database,
          title: "المعلومات التي نجمعها",
          content: `نجمع المعلومات التي تقدمها طوعاً عندما:
• تسجل حساباً
• تسجل في الدورات
• تحجز جلسات التقييم
• تشترك في نشرتنا الإخبارية
• تتواصل معنا عبر موقعنا
• تشارك في الاختبارات أو التقييمات

قد تشمل هذه المعلومات:
• الاسم ومعلومات الاتصال (البريد الإلكتروني، رقم الهاتف)
• معلومات الدفع (تتم معالجتها بشكل آمن عبر مزودي خدمات خارجيين)
• التفضيلات والأهداف التعليمية
• تفضيلات التواصل
• نتائج التقييم وتقدم الدورة`
        },
        {
          icon: Eye,
          title: "كيف نستخدم معلوماتك",
          content: `نستخدم المعلومات التي نجمعها من أجل:
• تقديم وتشغيل وصيانة خدماتنا
• معالجة المعاملات وإرسال المعلومات ذات الصلة
• إرسال معلومات إدارية وتحديثات ومواد ترويجية
• الرد على الاستفسارات وتقديم دعم العملاء
• تخصيص تجربة التعلم الخاصة بك
• تحسين موقعنا وخدماتنا
• الامتثال للالتزامات القانونية
• التواصل معك عبر واتساب أو البريد الإلكتروني بشأن دوراتك وتقييماتك`
        },
        {
          icon: Lock,
          title: "أمان البيانات",
          content: `نطبق إجراءات أمنية تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التعديل أو الإفصاح أو التدمير. تشمل هذه الإجراءات:
• تشفير SSL آمن لنقل البيانات
• تقييمات أمنية منتظمة
• وصول محدود للبيانات الشخصية من قبل الموظفين المصرح لهم فقط
• ممارسات تخزين بيانات آمنة

ومع ذلك، لا توجد طريقة نقل عبر الإنترنت أو تخزين إلكتروني آمنة بنسبة 100%، ولا يمكننا ضمان الأمان المطلق.`
        },
        {
          icon: Globe,
          title: "خدمات الطرف الثالث",
          content: `قد نشارك معلوماتك مع مزودي خدمات الطرف الثالث الذين يساعدوننا في تشغيل موقعنا وإدارة أعمالنا، بما في ذلك:
• معالجات الدفع (للتعامل الآمن مع المدفوعات)
• مزودي خدمات البريد الإلكتروني (للتواصل)
• مزودي التحليلات (لتحسين خدماتنا)
• منصات مؤتمرات الفيديو (للفصول الدراسية عبر الإنترنت)

هؤلاء الأطراف الثالثة ملزمون بالحفاظ على سرية معلوماتك واستخدامها فقط للأغراض التي نكشف عنها لهم.`
        },
        {
          icon: Mail,
          title: "حقوقك والاتصال بنا",
          content: `لديك الحق في:
• الوصول إلى معلوماتك الشخصية
• تصحيح البيانات غير الدقيقة
• طلب حذف بياناتك
• إلغاء الاشتراك في الاتصالات التسويقية
• سحب الموافقة في أي وقت

لأي أسئلة حول سياسة الخصوصية هذه أو لممارسة حقوقك، يرجى الاتصال بنا:

البريد الإلكتروني: info@canada-englishlife.com
الهاتف: +1 647 874 5620
العنوان: كندا

قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بأي تغييرات عن طريق نشر سياسة الخصوصية الجديدة على هذه الصفحة.`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_english-academy-lms/artifacts/fhrsr91w_profile_picture.png"
                alt="Canada English Life Academy"
                className="h-10 w-auto object-contain"
              />
              <span className="text-lg font-bold text-gray-900">Canada English Life</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              {t.backHome}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-500">{t.lastUpdated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 pt-1">{section.title}</h2>
                </div>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>© 2026 Canada English Life Academy. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
